import type {
  CounterpartyRole,
  DecaStatus,
  SignatureMethod,
  SignerRole,
  TenantRole,
} from "@deca/shared-types";

/**
 * Presentación de datos. Todo lo que se muestra al usuario en español pasa
 * por aquí, para que un estado no se escriba de dos formas distintas en dos
 * pantallas (SKILL.md sección 13: el idioma del documento y de la interfaz es
 * español).
 */

const DATE_TIME = new Intl.DateTimeFormat("es-ES", {
  dateStyle: "short",
  timeStyle: "short",
  timeZone: "Europe/Madrid",
});

const DATE_ONLY = new Intl.DateTimeFormat("es-ES", {
  dateStyle: "medium",
  timeZone: "Europe/Madrid",
});

const NUMBER = new Intl.NumberFormat("es-ES", { maximumFractionDigits: 2 });

export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return DATE_TIME.format(date);
}

export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return "—";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return DATE_ONLY.format(date);
}

/** Para los `<input type="date">`, que trabajan en fecha local sin zona. */
export function toDateInputValue(value: string | Date | null | undefined): string {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const formatter = new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Madrid" });
  return formatter.format(date);
}

export function formatKg(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return `${NUMBER.format(value)} kg`;
}

export function formatBytes(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${NUMBER.format(value / 1024)} KB`;
  return `${NUMBER.format(value / (1024 * 1024))} MB`;
}

/** Identificadores: se abrevian para la tabla, nunca se reinterpretan. */
export function shortId(value: string | null | undefined, length = 8): string {
  if (!value) return "—";
  return value.slice(0, length);
}

export function formatHash(value: string | null | undefined): string {
  if (!value) return "—";
  return value.slice(0, 16);
}

const DECA_STATUS_LABELS: Record<DecaStatus | "CERRADO", string> = {
  ACTIVO: "Activo",
  DESCARGA_DESACTIVADA: "Descarga desactivada",
  SUSTITUIDO: "Sustituido",
  CERRADO: "Cerrado",
};

export function decaStatusLabel(status: string | null | undefined): string {
  if (!status) return "—";
  return DECA_STATUS_LABELS[status as DecaStatus] ?? status;
}

/**
 * Tono del estado. `DESCARGA_DESACTIVADA` es aviso, **no** error: el documento
 * sigue conservado y accesible desde el panel (es la distinción que más se
 * malinterpreta de la norma).
 */
export function decaStatusTone(status: string | null | undefined): "ok" | "warn" | "neutral" | "danger" {
  if (status === "ACTIVO") return "ok";
  if (status === "DESCARGA_DESACTIVADA") return "warn";
  if (status === "CERRADO") return "danger";
  return "neutral";
}

const COUNTERPARTY_ROLE_LABELS: Record<CounterpartyRole, string> = {
  CARGADOR_CONTRACTUAL: "Cargador contractual",
  OPERADOR_INTERMEDIO: "Operador intermedio",
  TRANSPORTISTA_EFECTIVO: "Transportista efectivo",
  DESTINATARIO: "Destinatario",
};

export function counterpartyRoleLabel(role: string | null | undefined): string {
  if (!role) return "—";
  return COUNTERPARTY_ROLE_LABELS[role as CounterpartyRole] ?? role;
}

const TENANT_ROLE_LABELS: Record<TenantRole, string> = {
  ADMIN: "Administración",
  GESTOR: "Gestión",
  LECTOR: "Lectura",
  CONDUCTOR: "Conducción",
};

export function tenantRoleLabel(role: string | null | undefined): string {
  if (!role) return "—";
  return TENANT_ROLE_LABELS[role as TenantRole] ?? role;
}

const SIGNER_ROLE_LABELS: Record<SignerRole, string> = {
  CARGADOR: "Cargador",
  TRANSPORTISTA: "Transportista",
  OPERADOR_INTERMEDIO: "Operador intermedio",
};

export function signerRoleLabel(role: string | null | undefined): string {
  if (!role) return "—";
  return SIGNER_ROLE_LABELS[role as SignerRole] ?? role;
}

const SIGNATURE_METHOD_LABELS: Record<SignatureMethod, string> = {
  click_to_sign: "Aceptación simple",
  drawn: "Firma manuscrita",
  uploaded_certificate: "Certificado externo",
};

export function signatureMethodLabel(method: string | null | undefined): string {
  if (!method) return "—";
  return SIGNATURE_METHOD_LABELS[method as SignatureMethod] ?? method;
}

/** Acciones del `AuditLog` en lenguaje llano (el código no se muestra). */
const AUDIT_ACTION_LABELS: Record<string, string> = {
  "deca.created": "Documento creado",
  "deca.version_created": "Versión añadida",
  "deca.replaced": "Documento sustituido",
  "deca.incident_updated": "Incidencia modificada",
  "deca.signed": "Documento firmado",
  "deca.download_disabled": "Descarga desactivada",
  "counterparty.created": "Contraparte creada",
  "counterparty.updated": "Contraparte modificada",
  "vehicle.created": "Vehículo creado",
  "vehicle.updated": "Vehículo modificado",
  "api_client.created": "Clave de API creada",
  "api_client.deactivated": "Clave de API revocada",
  "template.logo_uploaded": "Logo actualizado",
  "template.updated": "Plantilla modificada",
};

export function auditActionLabel(action: string | null | undefined): string {
  if (!action) return "—";
  return AUDIT_ACTION_LABELS[action] ?? action;
}
